import { useEffect, useState } from "react";
import { Label, Button } from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";

type Props = {
  totalValves: number;
  ChakId: number;
  onBack: () => void;
};

const FULL_VALVE_COUNT = 14;

const days = [
  { name: "Mon",  bitIndex: 1 },
  { name: "Tue",  bitIndex: 2 },
  { name: "Wed",  bitIndex: 3 },
  { name: "Thur", bitIndex: 4 },
  { name: "Fri",  bitIndex: 5 },
  { name: "Sat",  bitIndex: 6 },
  { name: "Sun",  bitIndex: 7 },
];

const decimalToBits = (decimal: number, length: number): boolean[] => {
  const bits = decimal.toString(2).padStart(length, "0");
  return bits.split("").map((b) => b === "1");
};

const bitsToDecimal = (selected: number[], totalBits: number): string => {
  const arr = Array(totalBits).fill(0);
  selected.forEach((i) => (arr[totalBits - 1 - i] = 1));
  return parseInt(arr.join(""), 2).toString();
};

const ScheduelPopup = ({ totalValves, ChakId, onBack }: Props) => {
  const [scheduleNo,     setScheduleNo]     = useState<string>("");
  const [isEnable,       setIsEnable]       = useState<boolean>(false);
  const [startDate,      setStartDate]      = useState<Date | null>(null);
  const [endDate,        setEndDate]        = useState<Date | null>(null);
  const [startTime,      setStartTime]      = useState<string>("");
  const [duration,       setDuration]       = useState<string>("");
  const [volume,         setVolume]         = useState<string>("");
  const [selectedDays,   setSelectedDays]   = useState<number[]>([]);
  const [selectedValves, setSelectedValves] = useState<number[]>([]);

  // ── Fetch existing schedule ───────────────────────────────────
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const res = await ApiService.axiosApi(
          `/Schedule/GetScheduleByDeviceId/${ChakId}`,
          { method: "GET" }
        );
        if (res.success && res.data) {
          const d = res.data;
          setScheduleNo(String(d.scheduleNo ?? ""));
          setIsEnable(!!d.enable);
          if (d.startDate) setStartDate(new Date(Number(d.startDate) * 1000));
          if (d.endDate)   setEndDate(new Date(Number(d.endDate) * 1000));
          if (d.startTime) setStartTime(d.startTime);
          setDuration(String(d.duration ?? ""));
          setVolume(String(d.volume ?? ""));

          if (d.weekDays) {
            const bits = decimalToBits(Number(d.weekDays), 8);
            const sel: number[] = [];
            bits.forEach((b, i) => { if (b) sel.push(7 - i); });
            setSelectedDays(sel);
          }
          if (d.valveNumber) {
            const bits = decimalToBits(Number(d.valveNumber), FULL_VALVE_COUNT);
            const sel: number[] = [];
            bits.forEach((b, i) => { if (b) sel.push(FULL_VALVE_COUNT - 1 - i); });
            setSelectedValves(sel);
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchSchedule();
  }, [ChakId]);

  const toggleDay = (bitIndex: number, checked: boolean) => {
    setSelectedDays((prev) =>
      checked ? [...prev, bitIndex] : prev.filter((i) => i !== bitIndex)
    );
  };

  const toggleValve = (index: number, checked: boolean) => {
    setSelectedValves((prev) =>
      checked ? [...prev, index] : prev.filter((i) => i !== index)
    );
  };

  const postSchedule = async () => {
    try {
      const payload = {
        deviceId:    ChakId,
        scheduleNo:  parseInt(scheduleNo),
        enable:      isEnable,
        startDate:   startDate ? String(Math.floor(startDate.getTime() / 1000)) : "",
        endDate:     endDate   ? String(Math.floor(endDate.getTime()   / 1000)) : "",
        startTime,
        duration,
        volume,
        weekDays:    bitsToDecimal(selectedDays,   8),
        valveNumber: bitsToDecimal(selectedValves, FULL_VALVE_COUNT),
      };

      const res = await ApiService.axiosApi("Command/addschedule", {
        method: "POST",
        data: payload,
      });

      if (res.success) {
        toast.success("✅ Schedule saved");
        onBack();
      } else {
        toast.error("Failed to schedule");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "6px 10px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid #c5dede",
    background: "white",
    outline: "none",
  };

  const sectionLabel: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    color: "#2b5e5e",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
  };

  return (
    <div style={{ padding: "16px 20px" }}>

      {/* ── Enable ── */}
      <div className="d-flex align-items-center mb-3">
        <span style={{ fontSize: 13, marginRight: 10, color: "#2b5e5e", fontWeight: 600 }}>Enable:</span>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={isEnable}
            onChange={(e) => setIsEnable(e.target.checked)}
          />
          <span className="slider"></span>
        </label>
      </div>

      <hr style={{ borderColor: "#e0efef", margin: "10px 0" }} />

      {/* ── Dates + Time ── */}
      <div className="d-flex gap-3 mb-3">
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>Start Date</div>
          <DatePicker
            className="volume-scheduel always-bordered"
            dateFormat="dd/MM/yyyy"
            placeholderText="DD/MM/YYYY"
            selected={startDate}
            onChange={(d) => setStartDate(d)}
            isClearable
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>End Date</div>
          <DatePicker
            className="volume-scheduel always-bordered"
            dateFormat="dd/MM/yyyy"
            placeholderText="DD/MM/YYYY"
            selected={endDate}
            onChange={(d) => setEndDate(d)}
            isClearable
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>Start Time</div>
          <input
            type="time"
            style={inputStyle}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>
      </div>

      <hr style={{ borderColor: "#e0efef", margin: "10px 0" }} />

      {/* ── Week days ── */}
      <div style={sectionLabel}>Week Days</div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {days.map((day) => {
          const active = selectedDays.includes(day.bitIndex);
          return (
            <div
              key={day.bitIndex}
              onClick={() => toggleDay(day.bitIndex, !active)}
              style={{
                padding: "4px 14px",
                borderRadius: 20,
                border: `1.5px solid ${active ? "#2b5e5e" : "#c5dede"}`,
                background: active ? "#2b5e5e" : "white",
                color: active ? "white" : "#2b5e5e",
                fontSize: 12,
                fontWeight: active ? 700 : 400,
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.2s",
              }}
            >
              {day.name}
            </div>
          );
        })}
      </div>

      <hr style={{ borderColor: "#e0efef", margin: "10px 0" }} />

      {/* ── Schedule No / Duration / Volume ── */}
      <div className="d-flex gap-3 mb-3">
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>Schedule No</div>
          <input type="number" style={inputStyle} value={scheduleNo} onChange={(e) => setScheduleNo(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>Duration</div>
          <input type="number" style={inputStyle} value={duration} onChange={(e) => setDuration(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>Volume</div>
          <input type="number" style={inputStyle} value={volume} onChange={(e) => setVolume(e.target.value)} />
        </div>
      </div>

      <hr style={{ borderColor: "#e0efef", margin: "10px 0" }} />

      {/* ── Valves ── */}
      <div style={sectionLabel}>Valves</div>
      <div className="d-flex flex-wrap gap-2 mb-4">
        {Array.from({ length: totalValves }, (_, i) => {
          const active = selectedValves.includes(i);
          return (
            <div
              key={i}
              onClick={() => toggleValve(i, !active)}
              style={{
                padding: "4px 14px",
                borderRadius: 20,
                border: `1.5px solid ${active ? "#89AC46" : "#c5dede"}`,
                background: active ? "#89AC46" : "white",
                color: active ? "white" : "#2b5e5e",
                fontSize: 12,
                fontWeight: active ? 700 : 400,
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.2s",
              }}
            >
              V{i + 1}
            </div>
          );
        })}
      </div>

      {/* ── Footer buttons ── */}
      <div className="d-flex justify-content-end gap-2">
        <Button color="danger" size="sm" onClick={onBack}>Cancel</Button>
        <Button color="primary" size="sm" onClick={postSchedule}>Save Schedule</Button>
      </div>

    </div>
  );
};

export default ScheduelPopup;
